/**
 * Active IP Signature Management
 * Stores the current user's IP signature for consistent tracking
 */

const ACTIVE_IP_SIG_KEY = 'activeIpSig';

/**
 * Sets the active IP signature
 * @param {string} sig - IP signature hash
 */
export function setActiveIpSig(sig) {
  try {
    localStorage.setItem(ACTIVE_IP_SIG_KEY, sig);
    console.log('[activeIpSig] Set:', sig.slice(0, 16) + '...');
  } catch (error) {
    console.warn('⚠️ Failed to set activeIpSig:', error);
  }
}

/**
 * Gets the active IP signature
 * @returns {string|null} Active IP signature or null
 */
export function getActiveIpSig() {
  try {
    return localStorage.getItem(ACTIVE_IP_SIG_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Clears the active IP signature
 * Used for testing or when user wants to reset tracking
 */
export function clearActiveIpSig() {
  try {
    localStorage.removeItem(ACTIVE_IP_SIG_KEY);
    console.log('[activeIpSig] Cleared');
  } catch (error) {
    console.warn('⚠️ Failed to clear activeIpSig:', error);
  }
}

/**
 * Resolves the active IP signature with on-demand fallback
 * If localStorage is empty/blocked, fetches a fresh signature
 * @param {Function} getIpSignature - Function to fetch IP signature
 * @returns {Promise<string|null>} IP signature or null
 */
export async function resolveActiveIpSig(getIpSignature) {
  // Try cached signature first
  let sig = getActiveIpSig();
  if (sig) return sig;

  // Fallback: fetch fresh signature (handles localStorage cleared/blocked)
  sig = await getIpSignature().catch(() => null);
  if (sig) {
    setActiveIpSig(sig);
    console.log('[activeIpSig] Resolved on-demand:', sig.slice(0, 16) + '...');
  }

  return sig;
}
